import { wccModules } from 'enums/wccModules';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { BindingContext, Subscribable } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import './column.html';
import './column.scss';
import { GridItemModel } from './item';

export interface GridColumnModelConfig<T> {
    index: number
    items: Subscribable<Array<GridItemModel<T>>>
}

export class GridColumnModel<T = any> {
    removed = ko.observable(false);

    index: Subscribable<number>
    height: Subscribable<number>

    items: Subscribable<Array<GridItemModel<T>>>

    constructor(jsonModel: GridColumnModelConfig<T>) {
        this.index = ko.observable(jsonModel.index);
        this.items = ko.flattenComputed(jsonModel.items).extend({ trackArrayChanges: true });

        this.height = this.items.reduce((result, item) => result + item.height(), () => 0);
    }

    getView(predicate: Func<boolean, [GridItemModel<T>]>) {
        return new GridColumnModel({
            index: this.index(),
            items: this.items.filter(predicate)
        });
    }
}

@injectable()
export default class GridColumnVM<T> {
    private index: Subscribable<number>
    private items: Subscribable<Array<GridItemModel<T>>>

    bodyNode = ko.observable<HTMLElement>();

    constructor(
        @inject(wccModules.componentConfig) model: GridColumnModel<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.index = model.index;
        this.items = model.items;

        const isVisible = model.removed.not();

        effects.register(bodyNode => {
            if (bodyNode != undefined && document.body.contains(bodyNode)) {
                const bindingContext = this.getBindingContext(bodyNode);

                if (bindingContext != undefined) {
                    this.syncItems(this.items(), bodyNode, bindingContext);

                    return this.items.subscribe(items => this.syncItems(items, bodyNode, bindingContext))
                }
            }
        }, [this.bodyNode], false);

        ko.applyBindingsToNode(info.element, {
            'visible': isVisible,

            'css': {
                'grid-new__column': true
            }
        });
    }

    private syncItems(items: GridItemModel<T>[], body: HTMLElement, gridContext: BindingContext): void {
        const columnIdx = this.index();
        const reversedItems = [...items].reverse();

        let prevItem: GridItemModel<T> | undefined;

        for (const item of reversedItems) {
            item.insertBefore(body, prevItem, gridContext);
            item.attachedTo(columnIdx);

            prevItem = item;
        }
    }

    private getBindingContext(body: HTMLElement) {
        var bodyContext = ko.contextFor(body);

        if (bodyContext != undefined)
            return bodyContext.$parentContext?.$parentContext?.$parentContext;
    }
}